import React,{useRef,useState,useEffect} from 'react'
import {isTrial} from '../access'
import axios from 'axios'
import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import './plans.css'

/* Fallback catalogue, used only when the server cannot list products.
   These six are the BASKETBALL plans. Football has six of its own at different
   prices (Annual - Expert is 1320 there, not 990), so hardcoding these showed a
   football manager the wrong plans entirely. The list endpoint below is the real
   source; this survives purely so the screen still renders if it is unavailable. */
const PRODUCTS=[
  {uuid:'54e2cfa9f1262a5032f43e35451eefd5', period:'6 Months', tier:'Basic',  title:'6 Months - Basic',  price:420, descr:'Six months of scouting with your own list and your home country unlocked.'},
  {uuid:'9b7ece8b4e2ea08b29eec90b60ad5dd0', period:'6 Months', tier:'Pro',    title:'6 Months - Pro',    price:480, descr:'Six months of scouting with one extra country and your own TOP 10.'},
  {uuid:'2cb94f218eb08753ba8774c847f95911', period:'6 Months', tier:'Expert', title:'6 Months - Expert', price:540, descr:'Six months of scouting across every country in Europe.'},
  {uuid:'375676e78e03d59e5595ee48b3edd8eb', period:'Annual',   tier:'Basic',  title:'Annual - Basic',    price:770, descr:'A full year of scouting with your own list and your home country unlocked.'},
  {uuid:'3bbdd584bee55499b41806782e3eca5e', period:'Annual',   tier:'Pro',    title:'Annual - Pro',      price:880, descr:'A full year of scouting with one extra country and your own TOP 10.'},
  {uuid:'f650e1db6207eeb796c0411703f3635b', period:'Annual',   tier:'Expert', title:'Annual - Expert',   price:990, descr:'A full year of scouting across every country in Europe.'}
]

const PERIODS=['6 Months','Annual']

/* "6 Months - Pro" -> "Pro". The tier word is the last token after the dash. */
const tierOf=(title)=>{
  const tail=String(title||'').split('-').pop().trim()
  if (/expert/i.test(tail)) return 'Expert'
  if (/pro/i.test(tail)) return 'Pro'
  return 'Basic'
}

const descrFor=(tier,period)=>{
  const span=period==='Annual'?'A full year':'Six months'
  if (tier==='Expert') return span+' of scouting across every country in Europe.'
  if (tier==='Pro') return span+' of scouting with one extra country and your own '+topListLabel()+'.'
  return span+' of scouting with your own list and your home country unlocked.'
}

const RECOMMENDED='Pro'

/* A football squad is eleven, a basketball five, and the products say so
   themselves — the football Pro plan sells "Your own TOP11". The blurb
   beside them has to use the same number or it contradicts the thing it
   is describing. */
function topListLabel() {
  return parseInt(sessionStorage.getItem('SportID'), 10) === 3 ? 'TOP 11' : 'TOP 10'
}

/* Built per render rather than held as a constant, because one line of it
   depends on the sport — a football plan sells a TOP 11. */
function featuresFor(){
  const top=topListLabel()
  return {
    Basic:[
      'Creation of your list',
      'Unlimited downloads of player profiles in your home country',
      'Direct communication with players in your database'
    ],
    Pro:[
      'Everything in Basic',
      'Access to +1 country of your choice',
      'Your own '+top
    ],
    Expert:[
      'Everything in Pro',
      'Access across all of Europe'
    ]
  }
}

const BILLING_NOTE={
  '6 Months':'One payment, covers 6 months',
  'Annual':'One payment, covers 12 months'
}

/* "6Months - Basic", "6 Months - Pro", "Annual - Expert" must all compare
   cleanly, so strip everything that is not a letter or a digit. */
const normalise=(value)=>String(value===undefined||value===null?'':value).toLowerCase().replace(/[^a-z0-9]/g,'')

const priceOf=(item)=>{
  const n=Number(item&&item.price)
  return isNaN(n)?0:n
}

const formatPrice=(value)=>{
  const n=Number(value)
  if (isNaN(n)) return String(value)
  return n%1===0?String(n):n.toFixed(2)
}

const Check=()=>(
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12.5l5 5L20 6.5"/></svg>
)

export default function Plans() {
  const [items,setItems]=useState(PRODUCTS)
  const [period,setPeriod]=useState('6 Months')
  const [isLoading,setIsLoading]=useState(true)
  const [failed,setFailed]=useState(0)
  const alive=useRef(true)
  const periodTouched=useRef(false)

  useEffect(()=>{
    alive.current=true
    loadData()
    return ()=>{ alive.current=false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  /* Preferred path: ask the server for this manager's own sport.
     Falls back to the hardcoded basketball six if the endpoint is not deployed yet,
     so the screen keeps working against an older API. */
  const loadData=()=>{
    setIsLoading(true)

    const sportId=sessionStorage.getItem('SportID')
    const url=process.env.REACT_APP_API_SERVER+'manager/eshopproducts'
      +(sportId?('?SportID='+encodeURIComponent(sportId)):'')

    axios.get(url).then(res=>{
      const rows=(res.data&&res.data.err===0&&Array.isArray(res.data.results))?res.data.results:null
      if (!rows||rows.length===0) throw new Error('no list')
      if (!alive.current) return

      const mapped=rows.map(row=>{
        const months=Number(row.LicenceDurationMonths)
        const period=months>=12?'Annual':'6 Months'
        const known=PRODUCTS.find(p=>normalise(p.title)===normalise(row.Title))
        return {
          uuid:row.UUID,
          period,
          tier:tierOf(row.Title),
          title:row.Title,
          price:row.Price,
          /* Always computed, never taken from FALLBACK. FALLBACK is a
             basketball price list, and its titles normalise to the same
             keys as the football ones — so a football scout was being
             shown a basketball sentence, TOP 10 and all. */
          descr:descrFor(tierOf(row.Title),period),
          countries:row.NumberOfCountries
        }
      })

      setItems(mapped)
      setFailed(0)
      const current=findCurrent(mapped)
      if (current&&!periodTouched.current) setPeriod(current.period)
      setIsLoading(false)
    }).catch(()=>{
      if (!alive.current) return
      loadOneByOne()
    })
  }

  /* The original per-uuid path, kept as the fallback. */
  const loadOneByOne=()=>{
    Promise.all(PRODUCTS.map(product=>
      axios.get(process.env.REACT_APP_API_SERVER+'manager/eshopproducts/'+product.uuid)
      .then(res=>{
        const row=(res.data&&res.data.results&&res.data.results.length===1)?res.data.results[0]:null
        if (!row) return {...product, failed:true}
        return {
          ...product,
          title:row.Title?row.Title:product.title,
          price:(row.Price===undefined||row.Price===null||row.Price==='')?product.price:row.Price,
          // API Descr is an HTML <ul> duplicating the feature list below, so a
          // curated one-line summary is used for the card subtitle instead —
          // computed from tier and period so it follows the sport.
          descr:descrFor(product.tier,product.period),
          countries:row.NumberOfCountries
        }
      }).catch(()=>({...product, failed:true}))
    )).then(rows=>{
      if (!alive.current) return
      setItems(rows)
      setFailed(rows.filter(r=>r.failed).length)
      /* Land the user on the period they are already paying for. */
      const current=findCurrent(rows)
      if (current&&!periodTouched.current) setPeriod(current.period)
      setIsLoading(false)
    })
  }

  const findCurrent=(rows)=>{
    /* A trial is a preview of a plan, not ownership of it. Marking it
       'current' would lock the one button the evaluator most needs. */
    if (isTrial()) return null
    const owned=normalise(sessionStorage.getItem('ProductTitle'))
    if (!owned) return null
    return rows.find(row=>normalise(row.title)===owned)||null
  }

  const selectPeriod=(value)=>{
    periodTouched.current=true
    setPeriod(value)
  }

  const current=findCurrent(items)
  const currentPrice=current?priceOf(current):null
  const shown=items.filter(item=>item.period===period)
  const basic=shown.find(item=>item.tier==='Basic')
  const pro=shown.find(item=>item.tier==='Pro')
  const proGap=(basic&&pro)?priceOf(pro)-priceOf(basic):0

  /* No known current plan -> neutral wording; otherwise price decides. */
  const actionFor=(item)=>{
    if (currentPrice===null) return 'Choose'
    return priceOf(item)<currentPrice?'Downgrade':'Upgrade'
  }

  return (
    <div className="plans-page">

      <div className="plans-head">
        <div className="plans-headline">
          <div className="plans-sub">
            {isTrial()
              ?<span>You have been trying <b>{sessionStorage.getItem('ProductTitle')}</b>. Choose the plan that fits — every plan is a single payment, no auto-renewal.</span>
              :current
              ?<span>You are on <b>{current.title}</b> — change it below, it takes a minute.</span>
              :<span>Pick the coverage you need. Every plan is a single payment, no auto-renewal.</span>}
          </div>
          {failed>0
            ?<div className="plans-error">{failed===1?'1 plan':failed+' plans'} could not be refreshed — showing the last known price.</div>
            :null}
        </div>

        <div className="plans-switch" role="group" aria-label="Billing period">
          {PERIODS.map(value=>(
            <button
              key={value}
              type="button"
              className="toggle"
              aria-pressed={period===value}
              onClick={()=>selectPeriod(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-thirds plans-grid">
        {isLoading
          ?['Basic','Pro','Expert'].map(tier=>(
              <Panel key={tier} className="plan-card" title={tier}>
                <div className="plan-sk" aria-hidden="true">
                  <span className="skeleton sk-price"></span>
                  <span className="skeleton sk-text"></span>
                  <span className="skeleton sk-text short"></span>
                  <span className="skeleton sk-rule"></span>
                  <span className="skeleton sk-text"></span>
                  <span className="skeleton sk-text"></span>
                  <span className="skeleton sk-text short"></span>
                  <span className="skeleton sk-cta"></span>
                </div>
              </Panel>
            ))
          :shown.map(item=>{
              const isCurrent=!!(current&&current.uuid===item.uuid)
              const isRecommended=item.tier===RECOMMENDED
              const topListName=topListLabel()
              const features=featuresFor()
              const action=actionFor(item)
              const classes=['plan-card']
              if (isRecommended) classes.push('is-recommended')
              if (isCurrent) classes.push('is-current')

              return (
                <Panel
                  key={item.uuid}
                  className={classes.join(' ')}
                  title={item.tier}
                  subtitle={item.title}
                  actions={(isRecommended||isCurrent)
                    ?<>
                        {isRecommended?<span className="plan-flag">Recommended</span>:null}
                        {isCurrent?<span className="plan-badge">Current plan</span>:null}
                      </>
                    :null}
                >
                  <div className="plan-price">
                    <span className="plan-amount">{formatPrice(item.price)}</span>
                    <span className="plan-cur">€</span>
                    <span className="plan-per">{item.period==='Annual'?'/ year':'/ 6 months'}</span>
                  </div>

                  <div className="plan-descr">{item.descr}</div>

                  {isRecommended
                    ?<p className="callout">
                        {proGap>0
                          ?<span>Most users end up here: for <b>{formatPrice(proGap)}€ more than Basic</b>, you get access to a second favourite country and can create your own {topListName}.</span>
                          :<span>Most users end up here: a second favourite country and your own {topListName}, on top of Basic.</span>}
                      </p>
                    :null}

                  <ul className="plan-feats">
                    {features[item.tier].map(feature=>(
                      <li className="plan-feat" key={feature}>
                        <Check/>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="plan-foot">
                    {isCurrent
                      ?<Btn className="plan-cta" disabled aria-disabled="true">Current plan</Btn>
                      :<a
                          className={action==='Downgrade'?'btn-ui plan-cta':'btn-ui btn-accent plan-cta'}
                          href={'#/checkout/'+item.uuid}
                        >
                          {action==='Choose'?'Choose '+item.tier:action+' to '+item.tier}
                        </a>}
                    <div className="plan-meta">
                      {isCurrent?'Active on your account':BILLING_NOTE[item.period]}
                    </div>
                  </div>
                </Panel>
              )
            })}
      </div>
    </div>
  )
}
